/**
 * SendFaxInlineCard
 *
 * An inline card for composing and sending faxes, similar to SendFrontEmailInlineCard.
 * Shows fax number, cover page fields, attachments, and a send button.
 * Supports merging multiple documents into a single fax.
 */
import { FileText, Printer, Send, Trash2, Upload } from 'lucide-react';
import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useState,
} from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Textarea } from '@/components/ui/textarea';
import { useGetBlobsQuery } from '@/services/api/blobService';
import {
  type FaxTransmissionResponse,
  useSendFaxFromDocumentInstanceMutation,
  useSendMergedFaxMutation,
} from '@/services/api/faxService';

export interface SendFaxInlineCardRef {
  triggerSend: () => Promise<void>;
  isSending: boolean;
}

interface SendFaxInlineCardProps {
  matterId: string;
  documentId: string;
  defaultRecipient?: string;
  defaultCoverTo?: string;
  defaultCoverFrom?: string;
  defaultCoverMessage?: string;
  onSent?: (transmission: FaxTransmissionResponse) => void;
  className?: string;
  /** Optional footer content (e.g., communication history) */
  footerContent?: React.ReactNode;
  /** Custom label for the send button */
  sendButtonLabel?: string;
  /** Whether an external action is in progress (disables the send button) */
  isExternalLoading?: boolean;
  /** Optional initial additional blob attachments */
  initialAdditionalFiles?: { id: string; filename: string }[];
}

export const SendFaxInlineCard = forwardRef<
  SendFaxInlineCardRef,
  SendFaxInlineCardProps
>(function SendFaxInlineCard(
  {
    matterId,
    documentId,
    defaultRecipient = '',
    defaultCoverTo = '',
    defaultCoverFrom = '',
    defaultCoverMessage = '',
    onSent,
    className = '',
    footerContent,
    sendButtonLabel = 'Send Fax',
    isExternalLoading = false,
    initialAdditionalFiles = [],
  },
  ref,
) {
  const [recipientNumber, setRecipientNumber] = useState(defaultRecipient);
  const [recipientInput, setRecipientInput] = useState('');
  const [coverTo, setCoverTo] = useState(defaultCoverTo);
  const [coverFrom, setCoverFrom] = useState(defaultCoverFrom);
  const [coverMessage, setCoverMessage] = useState(defaultCoverMessage);
  const [additionalFiles, setAdditionalFiles] = useState<
    { id: string; filename: string }[]
  >(initialAdditionalFiles);
  const [fileQuery, setFileQuery] = useState('');

  const [sendFax, { isLoading: isSendingSingle }] =
    useSendFaxFromDocumentInstanceMutation();
  const [sendMergedFax, { isLoading: isSendingMerged }] =
    useSendMergedFaxMutation();
  const { data: blobs = [] } = useGetBlobsQuery(matterId);

  const isSending = isSendingSingle || isSendingMerged;

  // Filter blobs for file picker (only PDFs, exclude already selected)
  const filteredBlobs = blobs.filter(
    (blob) =>
      (blob.name?.toLowerCase().includes(fileQuery.toLowerCase()) ||
        blob.original_filename
          ?.toLowerCase()
          .includes(fileQuery.toLowerCase())) &&
      !additionalFiles.some((f) => f.id === blob.id),
  );

  // Parse comma-separated recipients into array
  const recipients = recipientNumber
    ? recipientNumber
        .split(',')
        .map((r) => r.trim())
        .filter(Boolean)
    : [];

  const isValidFaxNumber = (number: string): boolean => {
    const digits = number.replace(/\D/g, '');
    return digits.length >= 10;
  };

  const addRecipient = useCallback(
    (value: string) => {
      const trimmed = value.trim();
      if (!trimmed) return;

      if (isValidFaxNumber(trimmed) && !recipients.includes(trimmed)) {
        const newRecipients = [...recipients, trimmed];
        setRecipientNumber(newRecipients.join(', '));
        setRecipientInput('');
      } else if (!isValidFaxNumber(trimmed)) {
        toast.error('Please enter a valid fax number (at least 10 digits)');
      }
    },
    [recipients],
  );

  const removeRecipient = useCallback(
    (value: string) => {
      const newRecipients = recipients.filter((r) => r !== value);
      setRecipientNumber(newRecipients.join(', '));
    },
    [recipients],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter' || e.key === ',' || e.key === 'Tab') {
        e.preventDefault();
        addRecipient(recipientInput);
      } else if (
        e.key === 'Backspace' &&
        recipientInput === '' &&
        recipients.length > 0
      ) {
        removeRecipient(recipients[recipients.length - 1]);
      }
    },
    [addRecipient, recipientInput, recipients, removeRecipient],
  );

  const handleSendFax = useCallback(async () => {
    // Commit any pending input
    if (recipientInput.trim()) {
      addRecipient(recipientInput);
    }

    const finalRecipients =
      recipients.length > 0 ? recipients : [recipientInput.trim()];

    if (finalRecipients.length === 0 || !finalRecipients[0]) {
      toast.error('Enter at least one fax number');
      return;
    }

    if (!documentId && additionalFiles.length === 0) {
      toast.error('No document selected');
      return;
    }

    try {
      // Build cover message - use full message from textarea
      const fullCoverMessage = coverMessage.trim() || undefined;
      const recipientNum = finalRecipients[0].replace(/\D/g, '');

      let result: FaxTransmissionResponse;

      // Use merged endpoint if there are additional files, otherwise single document
      if (additionalFiles.length > 0) {
        // Merge all documents into a single fax
        result = await sendMergedFax({
          recipient_number: recipientNum,
          matter_id: matterId,
          document_instance_ids: documentId ? [documentId] : [],
          blob_ids: additionalFiles.map((f) => f.id),
          cover_message: fullCoverMessage,
          cover_page_to: coverTo.trim() || undefined,
          cover_page_from: coverFrom.trim() || undefined,
          request_type: 'general_document',
        }).unwrap();

        const totalDocs = (documentId ? 1 : 0) + additionalFiles.length;
        toast.success(
          `Fax sent successfully with ${totalDocs} document${totalDocs > 1 ? 's' : ''} merged`,
        );

        // Clear additional files after successful send
        setAdditionalFiles([]);
      } else {
        // Single document - use original endpoint
        result = await sendFax({
          recipient_number: recipientNum,
          matter_id: matterId,
          document_instance_id: documentId,
          cover_message: fullCoverMessage,
          request_type: 'general_document',
        }).unwrap();

        toast.success('Fax sent successfully');
      }

      onSent?.(result);
    } catch (error) {
      console.error('Failed to send fax:', error);
      toast.error('Failed to send fax. Please try again.');
    }
  }, [
    addRecipient,
    additionalFiles,
    coverFrom,
    coverMessage,
    coverTo,
    documentId,
    matterId,
    onSent,
    recipientInput,
    recipients,
    sendFax,
    sendMergedFax,
  ]);

  const canSend =
    (recipients.length > 0 || recipientInput.trim()) &&
    (documentId || additionalFiles.length > 0);

  // Expose triggerSend method via ref for external triggering
  useImperativeHandle(
    ref,
    () => ({
      triggerSend: handleSendFax,
      isSending,
    }),
    [handleSendFax, isSending],
  );

  return (
    <div
      className={`rounded-2xl border border-zinc-100 bg-white p-5 shadow-lg shadow-zinc-200/60 ${className}`}
    >
      <div className="mb-4 flex items-center gap-2 border-b border-zinc-100 pb-3">
        <Printer className="h-5 w-5 text-zinc-500" />
        <span className="text-sm font-semibold text-zinc-700">
          Send LOR via Fax
        </span>
      </div>

      <div className="space-y-4">
        {/* Fax Number */}
        <div className="space-y-1.5">
          <Label className="text-sm font-medium text-zinc-700">
            Fax Number
          </Label>
          <div className="flex flex-wrap items-center gap-1.5 rounded-md border border-zinc-200 bg-white px-2 py-1.5 focus-within:ring-2 focus-within:ring-blue-500">
            {recipients.map((number) => (
              <span
                key={number}
                className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-sm text-emerald-700"
              >
                <span className="max-w-[180px] truncate font-mono text-xs">
                  {number}
                </span>
                <button
                  type="button"
                  onClick={() => removeRecipient(number)}
                  className="ml-0.5 rounded-full p-0.5 hover:bg-emerald-100"
                  aria-label={`Remove ${number}`}
                >
                  <svg
                    className="h-3 w-3"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </span>
            ))}
            <input
              type="tel"
              value={recipientInput}
              onChange={(e) => setRecipientInput(e.target.value)}
              onKeyDown={handleKeyDown}
              onBlur={() => {
                if (recipientInput.trim()) {
                  addRecipient(recipientInput);
                }
              }}
              placeholder={recipients.length === 0 ? '+1 (555) 123-4567' : ''}
              className="min-w-[120px] flex-1 border-none bg-transparent py-1 text-sm outline-none placeholder:text-zinc-400"
            />
          </div>
          <p className="text-xs text-zinc-500">
            Press Enter to add multiple numbers
          </p>
        </div>

        {/* Cover Page Section */}
        <div className="border-t border-zinc-100 pt-4">
          <div className="mb-3 flex items-center gap-2">
            <FileText className="h-4 w-4 text-zinc-400" />
            <span className="text-sm font-medium text-zinc-600">
              Cover Page (optional)
            </span>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label htmlFor="fax-cover-to" className="text-xs text-zinc-500">
                To
              </Label>
              <Input
                id="fax-cover-to"
                type="text"
                placeholder="Records Department"
                value={coverTo}
                onChange={(e) => setCoverTo(e.target.value)}
                className="text-sm"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="fax-cover-from" className="text-xs text-zinc-500">
                From
              </Label>
              <Input
                id="fax-cover-from"
                type="text"
                placeholder="Your Name"
                value={coverFrom}
                onChange={(e) => setCoverFrom(e.target.value)}
                className="text-sm"
              />
            </div>
          </div>

          {/* Cover Message - editable */}
          <div className="mt-3 space-y-1">
            <Label
              htmlFor="fax-cover-message"
              className="text-xs text-zinc-500"
            >
              Cover Sheet Message
            </Label>
            <Textarea
              id="fax-cover-message"
              placeholder="Message to include on cover page..."
              value={coverMessage}
              onChange={(e) => setCoverMessage(e.target.value)}
              rows={4}
              className="text-sm resize-none"
            />
          </div>
        </div>

        {/* Additional Attachments Section */}
        <div className="border-t border-zinc-100 pt-4">
          <div className="mb-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Upload className="h-4 w-4 text-zinc-400" />
              <span className="text-sm font-medium text-zinc-600">
                Additional Attachments
              </span>
            </div>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="h-7 text-xs">
                  + Add File
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[280px] p-2" align="end">
                <input
                  className="w-full rounded-md border border-zinc-200 px-2 py-1.5 text-xs font-medium text-zinc-800"
                  placeholder="Search matter files..."
                  value={fileQuery}
                  onChange={(e) => setFileQuery(e.target.value)}
                  aria-label="Search matter files"
                />
                <div className="mt-1.5 max-h-40 overflow-auto">
                  {filteredBlobs.length === 0 ? (
                    <div className="px-2 py-2 text-[10px] text-zinc-400">
                      No files available
                    </div>
                  ) : (
                    <div className="space-y-0.5">
                      {filteredBlobs.map((blob) => (
                        <button
                          key={blob.id}
                          type="button"
                          onClick={() => {
                            const filename =
                              blob.original_filename ||
                              blob.name ||
                              'document.pdf';
                            setAdditionalFiles((prev) => [
                              ...prev,
                              { id: blob.id, filename },
                            ]);
                            setFileQuery('');
                          }}
                          className="w-full px-2 py-1.5 text-left text-xs font-medium rounded-md hover:bg-zinc-50 text-zinc-700"
                        >
                          <span className="truncate block">
                            {blob.original_filename || blob.name}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </PopoverContent>
            </Popover>
          </div>

          {/* Selected additional files */}
          {additionalFiles.length > 0 && (
            <div className="space-y-1.5">
              {additionalFiles.map((file) => (
                <div
                  key={file.id}
                  className="flex items-center justify-between rounded-md bg-blue-50 px-2 py-1.5 text-xs border border-blue-100"
                >
                  <span className="truncate font-medium text-blue-800">
                    {file.filename}
                  </span>
                  <button
                    type="button"
                    onClick={() =>
                      setAdditionalFiles((prev) =>
                        prev.filter((f) => f.id !== file.id),
                      )
                    }
                    className="text-zinc-400 hover:text-red-500"
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                </div>
              ))}
              <p className="text-xs text-zinc-500 mt-2">
                Documents will be merged into a single fax.
              </p>
            </div>
          )}

          {additionalFiles.length === 0 && (
            <p className="text-xs text-zinc-400">
              No additional files. Click &quot;+ Add File&quot; to attach more
              documents to merge into a single fax.
            </p>
          )}
        </div>

        {/* Send Button */}
        <div className="flex justify-end pt-2">
          <Button
            onClick={handleSendFax}
            disabled={!canSend || isSending || isExternalLoading}
            className="gap-2"
          >
            <Send className="h-4 w-4" />
            {isSending || isExternalLoading ? 'Sending...' : sendButtonLabel}
          </Button>
        </div>

        {/* Footer content (e.g., communication history) */}
        {footerContent && (
          <div className="mt-4 border-t border-zinc-100 pt-4">
            {footerContent}
          </div>
        )}
      </div>
    </div>
  );
});
